import { useState, useEffect, useCallback } from 'react';
import { Target, CheckCircle, Clock, AlertTriangle, AlertCircle, TrendingUp, Info, FileText, GitPullRequest, Sparkles, Plus, Trash2, X, Save, Loader2 } from 'lucide-react';
import { HeroSection } from '../HeroSection';
import { projectScopeService, type ProjectScopeItem as ApiScopeItem, type ScopeAIDraft, type ScopeDraftMilestone } from '../../services/projectScope';
import { projectsService, type Project } from '../../services/projects';
import { toast } from 'sonner';

interface ScopeSectionProps {
  projectId?: number;
}

interface ImpactAnalysis {
  costImpact: number;
  timelineImpact: number; // in days
  scopeImpact: string;
  riskImpact: string;
  resourceImpact: string;
  qualityImpact: string;
}

export function ScopeSection({ projectId }: ScopeSectionProps) {
  const [activeSubTab, setActiveSubTab] = useState<'definition' | 'management' | 'changes'>('definition');
  const [scopeItems, setScopeItems] = useState<ApiScopeItem[]>([]);
  const [outOfScopeItems, setOutOfScopeItems] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projectId) {
      setScopeItems([]);
      setOutOfScopeItems([]);
      return;
    }
    setLoading(true);
    Promise.all([
      projectScopeService.listScopeItems(projectId),
      projectScopeService.listOutOfScope(projectId),
    ])
      .then(([items, outOfScope]) => {
        setScopeItems(items);
        setOutOfScopeItems(outOfScope.map((o) => o.item_text));
      })
      .catch(() => toast.error('Failed to load scope'))
      .finally(() => setLoading(false));
  }, [projectId]);

  const scopeStats = {
    total: scopeItems.length,
    completed: scopeItems.filter((s) => s.status === 'Completed').length,
    inProgress: scopeItems.filter((s) => s.status === 'In Progress').length,
    planned: scopeItems.filter((s) => s.status === 'Planned').length,
  };

  const scopeItemsForUI = scopeItems.map((s) => ({
    id: `S-${String(s.id).padStart(3, '0')}`,
    category: s.category,
    item: s.item,
    description: s.description ?? '',
    status: s.status,
    deliverables: s.deliverables ?? [],
    acceptanceCriteria: s.acceptance_criteria ?? [],
    owner: s.owner ?? '',
  }));

  return (
    <div className="p-6 space-y-6">
      <HeroSection
        title="Project Scope"
        description="Scope definition, deliverables, and change management"
        variant="level4"
        metrics={[
          { label: 'Total Scope Items', value: scopeStats.total.toString(), icon: Target, iconColor: 'bg-blue-50' },
          { label: 'Completed', value: scopeStats.completed.toString(), icon: CheckCircle, iconColor: 'bg-green-50' },
          { label: 'In Progress', value: scopeStats.inProgress.toString(), icon: Clock, iconColor: 'bg-amber-50' },
          { label: 'Planned', value: scopeStats.planned.toString(), icon: AlertCircle, iconColor: 'bg-purple-50' },
        ]}
      />

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveSubTab('definition')}
            className={`px-6 py-3 text-sm transition-colors ${
              activeSubTab === 'definition'
                ? 'border-b-2 border-blue-600 text-blue-600 font-medium'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              <span>Scope Definition</span>
            </div>
          </button>
          <button
            onClick={() => setActiveSubTab('management')}
            className={`px-6 py-3 text-sm transition-colors ${
              activeSubTab === 'management'
                ? 'border-b-2 border-blue-600 text-blue-600 font-medium'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span>Scope Deliverables</span>
            </div>
          </button>
          <button
            onClick={() => setActiveSubTab('changes')}
            className={`px-6 py-3 text-sm transition-colors ${
              activeSubTab === 'changes'
                ? 'border-b-2 border-blue-600 text-blue-600 font-medium'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <GitPullRequest className="w-4 h-4" />
              <span>Change Requests</span>
            </div>
          </button>
        </div>

        <div className="p-8">
          {activeSubTab === 'definition' && (
            <ScopeDefinitionEditor
              projectId={projectId}
              outOfScopeItems={outOfScopeItems}
              onOutOfScopeChanged={() => {
                if (!projectId) return;
                projectScopeService
                  .listOutOfScope(projectId)
                  .then((o) => setOutOfScopeItems(o.map((it) => it.item_text)))
                  .catch((err) => {
                    // Non-blocking refresh after an out-of-scope edit;
                    // if it fails the user can just refresh the page.
                    // Log so debugging is possible.
                    console.warn('Failed to refresh out-of-scope items:', err)
                  });
              }}
            />
          )}

          {activeSubTab === 'management' && (
            <div className="space-y-6">
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <div className="text-sm text-gray-600 mb-2">Total Scope Items</div>
                  <div className="text-3xl mb-2">{scopeStats.total}</div>
                  <div className="text-xs text-gray-500">Major deliverables</div>
                </div>
                <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                  <div className="text-sm text-green-700 mb-2">Completed</div>
                  <div className="text-3xl text-green-700 mb-2">{scopeStats.completed}</div>
                  <div className="text-xs text-green-600">{scopeStats.total ? Math.round((scopeStats.completed / scopeStats.total) * 100) : 0}% done</div>
                </div>
                <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                  <div className="text-sm text-blue-700 mb-2">In Progress</div>
                  <div className="text-3xl text-blue-700 mb-2">{scopeStats.inProgress}</div>
                  <div className="text-xs text-blue-600">Active work</div>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <div className="text-sm text-gray-700 mb-2">Planned</div>
                  <div className="text-3xl text-gray-700 mb-2">{scopeStats.planned}</div>
                  <div className="text-xs text-gray-500">Future phases</div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-lg">In-Scope Deliverables</h2>
                </div>
                {loading ? (
                  <div className="p-8 text-center text-gray-500">Loading scope...</div>
                ) : (
                <div className="divide-y divide-gray-200">
                  {scopeItemsForUI.map((item) => (
                    <div key={item.id} className="p-6 hover:bg-gray-50">
                      <div className="flex items-start gap-4 mb-4">
                        <div className={`p-3 rounded-lg ${
                          item.status === 'Completed' ? 'bg-green-100' :
                          item.status === 'In Progress' ? 'bg-blue-100' :
                          'bg-gray-100'
                        }`}>
                          {item.status === 'Completed' ? (
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          ) : item.status === 'In Progress' ? (
                            <Clock className="w-6 h-6 text-blue-600" />
                          ) : (
                            <Target className="w-6 h-6 text-gray-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-mono text-sm font-medium text-gray-700">{item.id}</span>
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                              {item.category}
                            </span>
                            <h3 className="font-medium text-lg">{item.item}</h3>
                            <span className={`ml-auto px-3 py-1 rounded text-sm ${
                              item.status === 'Completed' ? 'bg-green-100 text-green-700' :
                              item.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {item.status}
                            </span>
                          </div>
                          <p className="text-gray-600 text-sm mb-4">{item.description}</p>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <div className="text-xs text-gray-500 font-medium mb-2">Deliverables</div>
                              <ul className="space-y-1">
                                {item.deliverables.map((del, idx) => (
                                  <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                                    <span className="text-blue-600 mt-1">•</span>
                                    <span>{del}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium mb-2">Acceptance Criteria</div>
                              <ul className="space-y-1">
                                {item.acceptanceCriteria.map((criteria, idx) => (
                                  <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                                    <CheckCircle className="w-3 h-3 text-green-600 mt-1 flex-shrink-0" />
                                    <span>{criteria}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 font-medium mb-2">Owner</div>
                              <div className="text-sm text-gray-700">{item.owner}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <h2 className="text-lg text-red-900">Out of Scope</h2>
                </div>
                <ul className="space-y-2">
                  {outOfScopeItems.map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-red-900">
                      <span className="text-red-600 font-bold mt-0.5">✕</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h2 className="text-lg mb-4">Scope Change Summary</h2>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl text-blue-600 mb-1">3</div>
                    <div className="text-sm text-gray-600">Approved Changes</div>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <div className="text-2xl text-yellow-600 mb-1">1</div>
                    <div className="text-sm text-gray-600">Pending Review</div>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl text-red-600 mb-1">2</div>
                    <div className="text-sm text-gray-600">Rejected</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSubTab === 'changes' && (
            <ChangeRequestsContent projectId={projectId} />
          )}
        </div>
      </div>
    </div>
  );
}

function ChangeRequestsContent({ projectId }: { projectId?: number }) {
  const [requests, setRequests] = useState<Array<{
    id: string;
    description: string;
    category: string;
    reason: string;
    impactAnalysis: ImpactAnalysis;
    approver: string;
    status: string;
    submittedDate: string;
    aiRecommendation?: { decision: string; confidence: number; reasoning: string[]; risks: string[]; benefits: string[] };
  }>>([]);
  const [crLoading, setCrLoading] = useState(false);

  useEffect(() => {
    if (!projectId) {
      setRequests([]);
      return;
    }
    setCrLoading(true);
    projectScopeService
      .listChangeRequests(projectId)
      .then((list) => {
        return setRequests(
          list.map((r) => ({
            id: `CR-${String(r.id).padStart(3, '0')}`,
            description: r.description,
            category: r.category,
            reason: r.reason ?? '',
            impactAnalysis: {
              costImpact: Number(r.impact_analysis?.costImpact ?? 0),
              timelineImpact: Number(r.impact_analysis?.timelineImpact ?? 0),
        
              // ✅ FIXED — now strings
              scopeImpact: String(r.impact_analysis?.scopeImpact ?? ''),
              riskImpact: String(r.impact_analysis?.riskImpact ?? ''),
              resourceImpact: String(r.impact_analysis?.resourceImpact ?? ''),
              qualityImpact: String(r.impact_analysis?.qualityImpact ?? ''),
            },
            approver: r.approver ?? '',
            status: r.status,
            submittedDate: r.submitted_date ?? '',
          }))
        );
      })
      .catch(() => setRequests([]))
      .finally(() => setCrLoading(false));
  }, [projectId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved': return 'bg-green-50 text-green-700 border-green-200';
      case 'Rejected': return 'bg-red-50 text-red-700 border-red-200';
      case 'Pending Review': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'Implemented': return 'bg-blue-50 text-blue-700 border-blue-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Scope': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'Time': return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'Cost': return 'bg-green-50 text-green-700 border-green-200';
      case 'Risk': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getChangeRequestColor = (index: number) => {
    const colors = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444'];
    return colors[index % colors.length];
  };

  const metrics = {
    total: requests.length,
    pending: requests.filter((r) => r.status === 'Pending Review').length,
    approved: requests.filter((r) => r.status === 'Approved').length,
    rejected: requests.filter((r) => r.status === 'Rejected').length,
    totalCostImpact: requests.filter((r) => r.status === 'Approved').reduce((sum, r) => sum + (r.impactAnalysis?.costImpact || 0), 0),
    totalTimeImpact: requests.filter((r) => r.status === 'Approved').reduce((sum, r) => sum + (r.impactAnalysis?.timelineImpact || 0), 0),
  };

  if (crLoading) {
    return <div className="py-8 text-center text-gray-500">Loading change requests...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-6 gap-4">
        <div className="bg-white p-5 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-600">Total</div>
            <Clock className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-3xl text-neutral-950">{metrics.total}</div>
        </div>
        <div className="bg-white p-5 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-600">Pending</div>
            <AlertTriangle className="w-4 h-4 text-yellow-600" />
          </div>
          <div className="text-3xl text-neutral-950">{metrics.pending}</div>
        </div>
        <div className="bg-white p-5 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-600">Approved</div>
            <CheckCircle className="w-4 h-4 text-green-600" />
          </div>
          <div className="text-3xl text-neutral-950">{metrics.approved}</div>
        </div>
        <div className="bg-white p-5 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-600">Rejected</div>
            <AlertCircle className="w-4 h-4 text-red-600" />
          </div>
          <div className="text-3xl text-neutral-950">{metrics.rejected}</div>
        </div>
        <div className="bg-white p-5 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-600">Cost Impact</div>
            <TrendingUp className="w-4 h-4 text-green-600" />
          </div>
          <div className="text-2xl text-neutral-950">${(metrics.totalCostImpact / 1000).toFixed(0)}K</div>
          <div className="text-xs text-gray-500">Approved changes</div>
        </div>
        <div className="bg-white p-5 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-600">Time Impact</div>
            <Clock className="w-4 h-4 text-orange-600" />
          </div>
          <div className="text-2xl text-neutral-950">{metrics.totalTimeImpact} days</div>
          <div className="text-xs text-gray-500">Approved changes</div>
        </div>
      </div>

      <div className="space-y-4">
        {requests.map((request, index) => (
          <div
            key={request.id}
            className="bg-white border border-gray-200 rounded-lg overflow-hidden"
            style={{ borderLeft: `4px solid ${getChangeRequestColor(index)}` }}
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm text-gray-500 font-mono">{request.id}</span>
                <span className={`px-3 py-1 rounded text-xs border ${getCategoryColor(request.category)}`}>
                  {request.category}
                </span>
                <span className={`px-3 py-1 rounded text-xs border ${getStatusColor(request.status)}`}>
                  {request.status}
                </span>
              </div>
              <h3 className="text-lg text-neutral-950 mb-2">{request.description}</h3>
              <p className="text-sm text-gray-600"><strong>Reason:</strong> {request.reason}</p>
              <div className="text-xs text-gray-500 mt-2">
                Submitted: {request.submittedDate} | Approver: {request.approver}
              </div>
            </div>

            {request.impactAnalysis && (
              <div className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  <h4 className="text-base text-neutral-950">Impact Analysis</h4>
                  <span className="ml-auto px-3 py-1 bg-purple-600 text-white rounded text-xs">AI-Generated</span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="text-sm text-gray-600 mb-1">Cost Impact</div>
                    <div className="text-2xl text-neutral-950">
                      ${request.impactAnalysis?.costImpact != null ? (Number(request.impactAnalysis.costImpact) / 1000).toFixed(0) : '0'}K
                    </div>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="text-sm text-gray-600 mb-1">Timeline Impact</div>
                    <div className="text-2xl text-neutral-950">{request.impactAnalysis?.timelineImpact ?? '—'} days</div>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="text-sm text-gray-600 mb-1">Scope Impact</div>
                    <div className="text-sm text-neutral-950">{request.impactAnalysis?.scopeImpact ?? '—'}</div>
                  </div>
                </div>
              </div>
            )}

            {request.aiRecommendation && (
              <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50">
                <div className="flex items-center gap-2 mb-4">
                  <AlertCircle className="w-5 h-5 text-purple-600" />
                  <h4 className="text-base text-neutral-950">AI Recommendation</h4>
                  <span className={`ml-auto px-3 py-1 rounded text-xs border ${
                    request.aiRecommendation.decision === 'Approve' ? 'bg-green-50 text-green-700 border-green-200' :
                    request.aiRecommendation.decision === 'Reject' ? 'bg-red-50 text-red-700 border-red-200' :
                    'bg-yellow-50 text-yellow-700 border-yellow-200'
                  }`}>
                    {request.aiRecommendation.decision}
                  </span>
                  <span className="px-3 py-1 bg-white rounded text-xs border border-gray-200">
                    {request.aiRecommendation.confidence}% Confidence
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h5 className="text-sm text-neutral-950 mb-2">💡 Reasoning</h5>
                    <ul className="space-y-1">
                      {request.aiRecommendation.reasoning.map((item, idx) => (
                        <li key={idx} className="text-xs text-gray-600 flex gap-2"><span>•</span><span>{item}</span></li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h5 className="text-sm text-neutral-950 mb-2">⚠️ Risks</h5>
                    <ul className="space-y-1">
                      {request.aiRecommendation.risks.map((item, idx) => (
                        <li key={idx} className="text-xs text-gray-600 flex gap-2"><span>•</span><span>{item}</span></li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h5 className="text-sm text-neutral-950 mb-2">✅ Benefits</h5>
                    <ul className="space-y-1">
                      {request.aiRecommendation.benefits.map((item, idx) => (
                        <li key={idx} className="text-xs text-gray-600 flex gap-2"><span>•</span><span>{item}</span></li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── Part 21 — Editable Scope Definition + AI scope drafter ──────────────────

interface ScopeDefinitionEditorProps {
  projectId?: number;
  outOfScopeItems: string[];
  onOutOfScopeChanged: () => void;
}

function ScopeDefinitionEditor({ projectId, outOfScopeItems, onOutOfScopeChanged }: ScopeDefinitionEditorProps) {
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Local editable buffers (separate from project so we don't autosave on
  // every keystroke — explicit Save button keeps the API surface predictable).
  const [objective, setObjective] = useState('');
  const [keyObjectives, setKeyObjectives] = useState<string[]>(['']);
  const [successCriteria, setSuccessCriteria] = useState<string[]>(['']);

  // Drafts surfaced from POST /scope/ai-draft. `null` = none yet.
  const [drafts, setDrafts] = useState<ScopeAIDraft | null>(null);
  const [drafting, setDrafting] = useState(false);

  const loadProject = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    projectsService
      .get(projectId)
      .then((p) => {
        setProject(p);
        setObjective(p.objective ?? '');
        setKeyObjectives(p.key_objectives && p.key_objectives.length ? [...p.key_objectives] : ['']);
        setSuccessCriteria(p.success_criteria && p.success_criteria.length ? [...p.success_criteria] : ['']);
      })
      .catch(() => toast.error('Failed to load project'))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const saveDefinition = async () => {
    if (!projectId) {
      toast.error('Save the project before editing scope');
      return;
    }
    setSaving(true);
    try {
      await projectsService.update(projectId, {
        objective: objective.trim() || null,
        key_objectives: keyObjectives.map((s) => s.trim()).filter(Boolean),
        success_criteria: successCriteria.map((s) => s.trim()).filter(Boolean),
      });
      toast.success('Scope definition saved');
      loadProject();
    } catch {
      toast.error('Failed to save scope definition');
    } finally {
      setSaving(false);
    }
  };

  const generateDrafts = async () => {
    if (!projectId) {
      toast.error('Save the project first');
      return;
    }
    const trimmedObjective = objective.trim();
    const trimmedKeys = keyObjectives.map((s) => s.trim()).filter(Boolean);
    if (!trimmedObjective && trimmedKeys.length === 0) {
      toast.error('Enter the project objective (or at least one key objective) before generating drafts.');
      return;
    }
    setDrafting(true);
    try {
      // Persist the objective + key objectives FIRST so the AI sees what the
      // user actually typed, even if they didn't hit Save Definition.
      await projectsService.update(projectId, {
        objective: trimmedObjective || null,
        key_objectives: trimmedKeys,
      });
      const result = await projectScopeService.aiDraft(projectId, {
        objective: trimmedObjective || undefined,
        key_objectives: trimmedKeys,
      });
      setDrafts(result);
    } catch (err: unknown) {
      const msg = (err as { response?: { status?: number } })?.response?.status === 402
        ? 'AI scope drafts require the Growth plan or higher.'
        : 'Could not generate drafts. Try again in a moment.';
      toast.error(msg);
    } finally {
      setDrafting(false);
    }
  };

  if (!projectId) {
    return (
      <div className="py-12 text-center text-gray-500 text-sm">
        Save the project before editing scope.
      </div>
    );
  }

  if (loading && !project) {
    return <div className="py-12 text-center text-gray-500 text-sm">Loading scope definition…</div>;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-gray-600">
            Define the project's objective and success criteria. When you've entered enough,
            click <span className="font-medium">Generate drafts with AI</span> to get suggested
            major milestones, project-wide success criteria, and commonly-missed out-of-scope items.
          </p>
        </div>
        <button
          onClick={saveDefinition}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 flex-shrink-0"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Definition
        </button>
      </div>

      {/* Objective */}
      <section>
        <label className="block text-sm font-medium text-neutral-950 mb-2">Objective</label>
        <p className="text-xs text-gray-500 mb-2">
          One paragraph describing what this project will achieve and why.
        </p>
        <textarea
          value={objective}
          onChange={(e) => setObjective(e.target.value)}
          rows={3}
          placeholder="e.g. Deliver a fully accessible client portal to reduce inbound support volume by 30% within 6 months of launch."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-[15px] leading-relaxed"
        />
      </section>

      {/* Key Objectives */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <div>
            <label className="block text-sm font-medium text-neutral-950">Key Objectives</label>
            <p className="text-xs text-gray-500">Sub-objectives that the project needs to hit on the way to the main objective.</p>
          </div>
          <button
            type="button"
            onClick={() => setKeyObjectives([...keyObjectives, ''])}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>
        <div className="space-y-2">
          {keyObjectives.map((obj, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="text"
                value={obj}
                onChange={(e) => {
                  const next = [...keyObjectives];
                  next[idx] = e.target.value;
                  setKeyObjectives(next);
                }}
                placeholder={`Key objective ${idx + 1}`}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {keyObjectives.length > 1 && (
                <button
                  type="button"
                  onClick={() => setKeyObjectives(keyObjectives.filter((_, i) => i !== idx))}
                  className="p-2 text-gray-400 hover:text-red-600"
                  aria-label="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* AI Generate */}
      <section className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-neutral-950 mb-1">AI scope drafter</h3>
              <p className="text-sm text-gray-600 max-w-xl">
                Given your objective and current scope items, the AI assistant will draft major
                milestones, project-wide success criteria, and commonly-missed out-of-scope items.
                You review each and accept the ones that fit.
              </p>
            </div>
          </div>
          <button
            onClick={generateDrafts}
            disabled={drafting}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:opacity-90 disabled:opacity-60 flex-shrink-0"
          >
            {drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {drafts ? 'Regenerate' : 'Generate drafts with AI'}
          </button>
        </div>
      </section>

      {drafts && (
        <AIDraftReviewer
          projectId={projectId}
          drafts={drafts}
          onClose={() => setDrafts(null)}
          onAccepted={(persistedSuccessCriteria) => {
            // Refresh local state from server for milestones/oos; success_criteria
            // we can update in-place to avoid a second round-trip flicker.
            if (persistedSuccessCriteria) setSuccessCriteria(persistedSuccessCriteria);
            onOutOfScopeChanged();
            loadProject();
          }}
        />
      )}

      {/* Success Criteria */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <div>
            <label className="block text-sm font-medium text-neutral-950">Project-wide Success Criteria</label>
            <p className="text-xs text-gray-500">Measurable outcomes that prove the project as a whole succeeded.</p>
          </div>
          <button
            type="button"
            onClick={() => setSuccessCriteria([...successCriteria, ''])}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>
        <div className="space-y-2">
          {successCriteria.map((crit, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <input
                type="text"
                value={crit}
                onChange={(e) => {
                  const next = [...successCriteria];
                  next[idx] = e.target.value;
                  setSuccessCriteria(next);
                }}
                placeholder={`Success criterion ${idx + 1} (e.g. "Support volume down 30% by month 6")`}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {successCriteria.length > 1 && (
                <button
                  type="button"
                  onClick={() => setSuccessCriteria(successCriteria.filter((_, i) => i !== idx))}
                  className="p-2 text-gray-400 hover:text-red-600"
                  aria-label="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Out of Scope (read-only here; the Management tab still owns CRUD) */}
      <section>
        <label className="block text-sm font-medium text-neutral-950 mb-2">Out of Scope</label>
        <p className="text-xs text-gray-500 mb-3">
          Captured exclusions. Manage the full list (add / delete) from the <span className="font-medium">Scope Deliverables</span> tab.
        </p>
        {outOfScopeItems.length === 0 ? (
          <p className="text-sm text-gray-400 italic">None captured yet. Use AI drafter above to seed this list.</p>
        ) : (
          <ul className="space-y-2">
            {outOfScopeItems.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                <Info className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}


// ─── Draft review modal ─────────────────────────────────────────────────────

interface AIDraftReviewerProps {
  projectId: number;
  drafts: ScopeAIDraft;
  onClose: () => void;
  onAccepted: (newSuccessCriteria: string[] | null) => void;
}

function AIDraftReviewer({ projectId, drafts, onClose, onAccepted }: AIDraftReviewerProps) {
  // Each draft starts toggled-on. User can flip off any individual item they
  // don't want before hitting Accept.
  const [milestoneOn, setMilestoneOn] = useState<boolean[]>(() => drafts.milestones.map(() => true));
  const [criteriaOn, setCriteriaOn] = useState<boolean[]>(() => drafts.success_criteria.map(() => true));
  const [oosOn, setOosOn] = useState<boolean[]>(() => drafts.out_of_scope.map(() => true));
  // Allow inline edits before accept.
  const [milestoneText, setMilestoneText] = useState<ScopeDraftMilestone[]>(() => drafts.milestones.map((m) => ({ ...m })));
  const [criteriaText, setCriteriaText] = useState<string[]>(() => [...drafts.success_criteria]);
  const [oosText, setOosText] = useState<string[]>(() => [...drafts.out_of_scope]);
  const [submitting, setSubmitting] = useState(false);

  const acceptedMilestones = milestoneText.filter((_, i) => milestoneOn[i] && milestoneText[i].name.trim());
  const acceptedCriteria = criteriaText.filter((_, i) => criteriaOn[i] && criteriaText[i].trim());
  const acceptedOos = oosText.filter((_, i) => oosOn[i] && oosText[i].trim());
  const totalAccepted = acceptedMilestones.length + acceptedCriteria.length + acceptedOos.length;

  const handleAccept = async () => {
    if (totalAccepted === 0) {
      toast.error('Select at least one item to accept');
      return;
    }
    setSubmitting(true);
    try {
      const res = await projectScopeService.acceptDrafts(projectId, {
        milestones: acceptedMilestones.map((m) => ({
          name: m.name.trim(),
          description: (m.description || '').trim(),
          due_date_offset_days: m.due_date_offset_days,
        })),
        success_criteria: acceptedCriteria.map((c) => c.trim()),
        out_of_scope: acceptedOos.map((o) => o.trim()),
      });
      toast.success(
        `Accepted: ${res.milestones_created} milestone(s), ${acceptedCriteria.length} criterion/criteria, ${res.out_of_scope_created} exclusion(s).`
      );
      // We don't get the final success_criteria list back so reload via callback.
      onAccepted(null);
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { status?: number } })?.response?.status === 402
        ? 'AI scope acceptance requires the Growth plan or higher.'
        : 'Failed to save accepted drafts';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-medium">Review AI drafts</h2>
            <span className="text-sm text-gray-500">— uncheck anything you don't want</span>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Milestones */}
          <section>
            <h3 className="text-base font-medium mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-blue-600" />
              Major Milestones
              <span className="text-xs text-gray-500 ml-1">(dates relative to project start)</span>
            </h3>
            {drafts.milestones.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No milestones suggested.</p>
            ) : (
              <div className="space-y-2">
                {milestoneText.map((m, idx) => (
                  <div key={idx} className={`border rounded-lg p-3 ${milestoneOn[idx] ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200 bg-gray-50 opacity-60'}`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={milestoneOn[idx]}
                        onChange={(e) => {
                          const next = [...milestoneOn];
                          next[idx] = e.target.checked;
                          setMilestoneOn(next);
                        }}
                        className="mt-2 w-4 h-4 text-blue-600 rounded"
                      />
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={m.name}
                            onChange={(e) => {
                              const next = [...milestoneText];
                              next[idx] = { ...next[idx], name: e.target.value };
                              setMilestoneText(next);
                            }}
                            className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!milestoneOn[idx]}
                          />
                          <div className="flex items-center gap-1 text-xs text-gray-600">
                            <input
                              type="number"
                              min={1}
                              max={365}
                              value={m.due_date_offset_days}
                              onChange={(e) => {
                                const next = [...milestoneText];
                                next[idx] = { ...next[idx], due_date_offset_days: parseInt(e.target.value) || 30 };
                                setMilestoneText(next);
                              }}
                              className="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                              disabled={!milestoneOn[idx]}
                            />
                            <span>days from start</span>
                          </div>
                        </div>
                        {m.description && (
                          <textarea
                            value={m.description}
                            onChange={(e) => {
                              const next = [...milestoneText];
                              next[idx] = { ...next[idx], description: e.target.value };
                              setMilestoneText(next);
                            }}
                            rows={2}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!milestoneOn[idx]}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Success Criteria */}
          <section>
            <h3 className="text-base font-medium mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              Project-wide Success Criteria
            </h3>
            {drafts.success_criteria.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No criteria suggested.</p>
            ) : (
              <div className="space-y-2">
                {criteriaText.map((c, idx) => (
                  <div key={idx} className={`flex items-center gap-2 border rounded-lg p-2 ${criteriaOn[idx] ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-gray-50 opacity-60'}`}>
                    <input
                      type="checkbox"
                      checked={criteriaOn[idx]}
                      onChange={(e) => {
                        const next = [...criteriaOn];
                        next[idx] = e.target.checked;
                        setCriteriaOn(next);
                      }}
                      className="w-4 h-4 text-green-600 rounded"
                    />
                    <input
                      type="text"
                      value={c}
                      onChange={(e) => {
                        const next = [...criteriaText];
                        next[idx] = e.target.value;
                        setCriteriaText(next);
                      }}
                      className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      disabled={!criteriaOn[idx]}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Out of Scope */}
          <section>
            <h3 className="text-base font-medium mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              Out of Scope
            </h3>
            {drafts.out_of_scope.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No exclusions suggested.</p>
            ) : (
              <div className="space-y-2">
                {oosText.map((o, idx) => (
                  <div key={idx} className={`flex items-center gap-2 border rounded-lg p-2 ${oosOn[idx] ? 'border-red-200 bg-red-50/30' : 'border-gray-200 bg-gray-50 opacity-60'}`}>
                    <input
                      type="checkbox"
                      checked={oosOn[idx]}
                      onChange={(e) => {
                        const next = [...oosOn];
                        next[idx] = e.target.checked;
                        setOosOn(next);
                      }}
                      className="w-4 h-4 text-red-600 rounded"
                    />
                    <input
                      type="text"
                      value={o}
                      onChange={(e) => {
                        const next = [...oosText];
                        next[idx] = e.target.value;
                        setOosText(next);
                      }}
                      className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                      disabled={!oosOn[idx]}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-between items-center sticky bottom-0 bg-white">
          <div className="text-sm text-gray-600">
            {totalAccepted} item{totalAccepted === 1 ? '' : 's'} selected
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={handleAccept}
              disabled={submitting || totalAccepted === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Accept selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
